import { isAxiosError } from 'axios';
import { SnackbarParams } from '@/components/common/snackbar/use-snackbar';
import { DownloadActionDataError } from '@/types';
import {
  createWorkbookWithSheet,
  downloadWorkbook,
  externalUrlHyperlinkValue,
} from '@/utils/excel';
import { isHttpUrl } from '@/utils/parsers';
import { filterPestNewsCategories } from './news_category';

type ActionDataItem = {
  [key: string]: string | null;
};

export type ActionDataResponse = {
  headers: string[];
  data: ActionDataItem[];
};

export const createWorkbook = async (
  data: ActionDataResponse['data'],
  headers: string[],
) => {
  const updatedData = data.map(item => {
    if (item['PESTカテゴリー'] === null) return item;
    const updatedCategories = filterPestNewsCategories(
      item['PESTカテゴリー'].split(',').map(item => item.trim()),
    ).join(',');
    return {
      ...item,
      PESTカテゴリー: updatedCategories,
    };
  });

  const { workbook, worksheet } = await createWorkbookWithSheet({
    sheetName: 'アクション履歴',
  });

  if (updatedData.length > 0) {
    // ヘッダー追加とフォーマット
    worksheet.columns = headers.map(header => {
      return { key: header };
    });
    worksheet.addRow(headers);
    for (let c = 1; c <= worksheet.columnCount; c++) {
      const cell = worksheet.getCell(1, c);
      cell.font = { bold: true };
    }

    worksheet.addRows(updatedData);
    // ハイパーリンク作成
    const urlColumnIndex = headers.findIndex(header =>
      header.toLowerCase().includes('url'),
    );

    // ハイパーリンク作成
    if (urlColumnIndex !== -1) {
      for (let r = 2; r <= worksheet.rowCount; r++) {
        // headerはskipして2から見る
        const cell = worksheet.getCell(r, urlColumnIndex + 1);
        const cellValue = cell.value?.toString();
        if (isHttpUrl(cellValue)) {
          cell.value = externalUrlHyperlinkValue(cellValue, cellValue);
          cell.font = {
            color: { argb: '000000FF' },
            underline: true,
          };
        }
      }
    }
  }
  return workbook;
};

export const downloadActionData = async (
  filename: string,
  data: ActionDataResponse['data'],
  format: 'excel' | 'csv',
  headers: string[],
): Promise<void> => {
  const workbook = await createWorkbook(data, headers);
  await downloadWorkbook(workbook, {
    filename,
    format,
    csvOptions: format === 'csv' ? { writeBOM: true } : undefined,
  });
};

export const handleDownloadError = (error: unknown): string => {
  if (isAxiosError<DownloadActionDataError>(error)) {
    if (
      error.response?.status === 400 &&
      error.response.data.type === 'result_too_large'
    ) {
      return 'エクスポートの上限（1万行）に達しています。お手数ですが、期間を分けてご利用ください。';
    }
  }

  return 'エラーが発生しました。しばらく時間をおいてお試しください。解消しない場合はサポートまでお問い合わせください。';
};

export const noDataSnackbarProps: SnackbarParams = {
  message: '指定期間内に該当するデータは存在しません。',
  description: 'データを表示するには、この期間にアクションが必要です。',
  type: 'info',
};
